import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Instagram, Linkedin, Mail, Phone, MapPin, ArrowRight, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAV = [
  { label: "About", to: "/about" },
  { label: "ForesightFlow", to: "/foresightflow" },
  { label: "Team", to: "/team" },
  { label: "Blogs", to: "/blogs" },
];

const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email: email.trim() });
      toast.success("You're on the list.", {
        description: "We'll send occasional, genuinely useful notes. No spam.",
      });
      setEmail("");
    } catch (err) {
      const msg =
        err?.response?.status === 422
          ? "Please enter a valid email address."
          : "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="footer" className="relative bg-[#FAF8F5] border-t border-[rgba(28,28,30,0.1)]">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 py-20 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Brand + newsletter */}
          <div className="lg:col-span-5">
            <div className="flex items-baseline gap-2">
              <span className="font-display text-3xl font-semibold tracking-tight text-[#1C1C1E]">
                Jay Shah
              </span>
              <span className="font-body text-[10px] uppercase tracking-[0.28em] text-[#3E4A5C]">
                Consultancy
              </span>
            </div>
            <p className="mt-4 max-w-sm font-display text-xl italic text-[#3E4A5C]">
              Your business is talking. We help you listen.
            </p>

            <form onSubmit={subscribe} className="mt-8 max-w-sm" data-testid="newsletter-form">
              <label className="font-body text-[10px] uppercase tracking-[0.22em] text-[#3E4A5C]">
                Get occasional insights
              </label>
              <div className="mt-3 flex items-center border-b border-[#1C1C1E]/25 focus-within:border-[#B39B77] transition-colors duration-300">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@yourstore.com"
                  data-testid="newsletter-input"
                  className="flex-1 bg-transparent py-3 font-body text-base text-[#1C1C1E] placeholder:text-[#3E4A5C]/50 outline-none"
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="newsletter-submit"
                  aria-label="Subscribe"
                  className="p-2 text-[#1C1C1E] hover:text-[#B39B77] transition-colors duration-300 disabled:opacity-50"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowRight className="w-5 h-5" />}
                </button>
              </div>
            </form>
          </div>

          {/* Nav */}
          <div className="lg:col-span-3">
            <p className="font-body text-[10px] uppercase tracking-[0.22em] text-[#3E4A5C] mb-5">
              Navigate
            </p>
            <ul className="space-y-3">
              {NAV.map((n) => (
                <li key={n.label}>
                  <Link
                    to={n.to}
                    data-testid={`footer-link-${n.label.toLowerCase()}`}
                    className="font-body text-base text-[#1C1C1E]/80 hover:text-[#1C1C1E] link-underline"
                  >
                    {n.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-4">
            <p className="font-body text-[10px] uppercase tracking-[0.22em] text-[#3E4A5C] mb-5">
              Get in touch
            </p>
            <ul className="space-y-4 font-body text-base text-[#1C1C1E]/80">
              <li>
                <a
                  href="mailto:experts.jsc@gmail.com"
                  data-testid="footer-email"
                  className="inline-flex items-center gap-3 hover:text-[#B39B77] transition-colors duration-300"
                >
                  <Mail className="w-4 h-4 text-[#3E4A5C]" strokeWidth={1.6} />
                  experts.jsc@gmail.com
                </a>
              </li>
              <li>
                <a
                  href="tel:+919168597272"
                  data-testid="footer-phone"
                  className="inline-flex items-center gap-3 hover:text-[#B39B77] transition-colors duration-300"
                >
                  <Phone className="w-4 h-4 text-[#3E4A5C]" strokeWidth={1.6} />
                  +91 91685 97272
                </a>
              </li>
              <li className="inline-flex items-center gap-3">
                <MapPin className="w-4 h-4 text-[#3E4A5C]" strokeWidth={1.6} />
                Pune, India
              </li>
            </ul>

            <div className="mt-7 flex items-center gap-3">
              <a
                href="https://www.instagram.com/jayshahconsultancy?igsh=Zng1eDVkeXpkOGM5"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram"
                aria-label="Instagram"
                className="w-10 h-10 inline-flex items-center justify-center rounded-full border border-[#1C1C1E]/15 text-[#1C1C1E] hover:bg-[#1C1C1E] hover:text-[#FAF8F5] transition-colors duration-300"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href="https://www.linkedin.com/company/jay-shah-consultancy/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-linkedin"
                aria-label="LinkedIn"
                className="w-10 h-10 inline-flex items-center justify-center rounded-full border border-[#1C1C1E]/15 text-[#1C1C1E] hover:bg-[#1C1C1E] hover:text-[#FAF8F5] transition-colors duration-300"
              >
                <Linkedin className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-[rgba(28,28,30,0.1)] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <p className="font-body text-xs text-[#3E4A5C]">
            © {new Date().getFullYear()} Jay Shah Consultancy. All rights reserved.
          </p>
          <p className="font-body text-xs text-[#3E4A5C]/70">
            ForesightFlow — Business intelligence for fashion retail.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
